import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../services/supabase';
import type { Achievement, UserAchievement, Profile } from '../types';

const fetchProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw new Error(error.message);
  return data;
};

const fetchAchievements = async (userId: string): Promise<(UserAchievement & { achievements: Achievement })[]> => {
  const { data, error } = await supabase
    .from('user_achievements')
    .select(`*, achievements (*)`)
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
};

const updateProfile = async (userId: string, updates: Partial<Profile>) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const useProfile = () => {
  const { user, profile: authProfile, setProfile } = useAuthStore();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => fetchProfile(user!.id),
    enabled: !!user,
    initialData: authProfile,
  });

  const achievementsQuery = useQuery({
    queryKey: ['achievements', user?.id],
    queryFn: () => fetchAchievements(user!.id),
    enabled: !!user,
  });

  const updateProfileMutation = useMutation({
    mutationFn: (updates: Partial<Profile>) => updateProfile(user!.id, updates),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', user?.id], data);
      setProfile(data); // Update auth store
    },
  });

  return {
    profile: profileQuery.data,
    achievements: achievementsQuery.data ?? [],
    isLoadingProfile: profileQuery.isLoading,
    isLoadingAchievements: achievementsQuery.isLoading,
    isUpdatingProfile: updateProfileMutation.isPending,
    updateProfile: updateProfileMutation.mutateAsync,
  };
};